import { inject, Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import {
  Article,
  ArticleSubItem,
  BasicSearchResponse,
  DetailedSearch,
  DetailSearchItem,
  DetailSearchResult,
  HeaderItemsRequest,
  hierarchy,
  ItemDetailRequest,
  PrintOrEmailRequest,
  ResultRow,
  ResultRowType,
  SearchPublicationItem,
  SearchRequest,
  SearchResultArticleGrouping,
  SearchResultList,
  SuggestionsResponse,
} from '@shared/components/organisms/search-bar/models/search.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ClassificationContent } from '@shared/components/molecules/class-details/models/class-details.model';
import { CLASSIFICATION_CONTENT_RESPONSE } from '@shared/components/molecules/class-details/models/class-details.mock';
import { IResponse } from '@shared/models/base/http-response.model';
import { environment } from '@env/environment';
import { ArticleChangeHistoryDetailsContainer } from '@shared/components/molecules/article-details-history/models/article-change-history-details.model';
import { ItemDetailContent } from '@shared/components/molecules/item-details/models/item-details.model';
import { FileDownloadService } from '@shared/services/file-download.service';

@Injectable({
  providedIn: 'root',
})
export class SearchService {

  http = inject(HttpClient);
  private readonly fileDownloadService: FileDownloadService = inject(FileDownloadService);
  isDownloadingPublication: boolean;

  getSearchSuggestions(request: SearchRequest): Observable<IResponse<SuggestionsResponse>> {
    return this.http.post<IResponse<SuggestionsResponse>>(
      new URL('/search/suggestionsearch', environment.apiUrl).href,
      request,
    );
  }

  search(request: SearchRequest): Observable<IResponse<BasicSearchResponse>> {
    return this.http.post<IResponse<BasicSearchResponse>>(new URL('/search/search', environment.apiUrl).href, request);
  }

  detailedSearch(request: SearchRequest): Observable<IResponse<DetailedSearch>> {
    return this.http.post<IResponse<DetailedSearch>>(new URL('/search/detailsearch', environment.apiUrl).href, request);
  }

  getPublicationDropDownList(): Observable<IResponse<SearchPublicationItem[]>> {
    return this.http.get<IResponse<SearchPublicationItem[]>>(new URL('/search/searchlist', environment.apiUrl).href);
  }

  // getArticleDetails(articleId: string): Observable<IResponse<ArticleDetails>> {
  //   return of(ARTICLE_DETAILS_RESPONSE).pipe(delay(500));

  // }
  getArticleDetails(request: ItemDetailRequest): Observable<IResponse<Article[]>> {
    return this.http.post<IResponse<Article[]>>(
      new URL('/search/detail', environment.apiUrl).href,
      request
    );
  }

  getArticleModalDetails(request: ItemDetailRequest): Observable<IResponse<Article[]>> {
    return this.http.post<IResponse<Article[]>>(
      new URL('/search/modaldetail', environment.apiUrl).href,
      request
    );
  }

  getArticleMoreDetails(request: HeaderItemsRequest): Observable<IResponse<Article[]>> {
    return this.http.post<IResponse<Article[]>>(
      //new URL('/search/articleunderheading', environment.apiUrl).href,
      new URL('/search/articlenext', environment.apiUrl).href,
      request,
    );
  }

  getClassificationDetails(classId: number): Observable<IResponse<ClassificationContent>> {
    return of(CLASSIFICATION_CONTENT_RESPONSE).pipe(delay(500));
  }

  getItemDetails(searchItem: ItemDetailRequest): Observable<IResponse<ItemDetailContent>> {
    return this.http.post<IResponse<ItemDetailContent>>(
      new URL('/search/itemdetail', environment.apiUrl).href,
      searchItem,
    );
  }

  getArticleDetailsHistory(articleId: string): Observable<IResponse<ArticleChangeHistoryDetailsContainer>> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<IResponse<ArticleChangeHistoryDetailsContainer>>(
      new URL('/publication/GetArticleChangeHistoryDetails', environment.apiUrl).href,
      JSON.stringify(articleId),
      { headers }
    );
  }

  addMarksToItemDetailContent(item: ItemDetailContent, searchText: string): ItemDetailContent {
    const resultLine: ItemDetailContent =
    {
      itemNumber: item.itemNumber,
      itemText: item.itemText,
      title: item.title,
      subTitle: item.subTitle,
      content: [],
    };

    for (let txtItem of item.content ?? []) {
      // Add highlight to search text in descriptionText
      txtItem = this.highlightSearchTextWithDOM(txtItem, searchText);
      // set path for any images
      resultLine.content.push(this.updateImageSrcPaths(txtItem));
    }
    return resultLine;
  }

  printRuleOrPackage(searchItem: PrintOrEmailRequest): Observable<Blob> {
    return this.http.post(
      new URL('/search/PrintRuleOrPackage', environment.apiUrl).href,
      searchItem,
      { responseType: 'blob' }
    );
  }

  emailSearchItem(searchItem: PrintOrEmailRequest): Observable<IResponse<boolean>> {
    return this.http.post<IResponse<boolean>>(
      new URL('/search/EmailSearchItem', environment.apiUrl).href,
      searchItem
    );
  }

  /**
   * Converts an Article object to a list of ResultRow objects for UI display.
   * @param article
   * @param searchText
   */
  transformArticleToResultRows(article: Article, searchText: string): ResultRow[] {
    let resultLines: ResultRow[] = [];

    if (article.grouping !== undefined && article.grouping?.headingNumber) {
      // set reference mark path
      article.grouping.html = this.updateSrcForDataMark(article.grouping.html ?? "");
      article.grouping.html = this.addLinkClassToDataAttributes(article.grouping.html);
      // set path for any images
      article.grouping.html = this.updateImageSrcPaths(article.grouping.html);

      resultLines.push({
        item: article.grouping.headingNumber,
        groupingHeading: article.grouping.subHeadingHtml ?? "",
        groupingHeadingNumber: article.grouping?.headingNumber ?? '',
        article: "SubHeading",
        isSearchMatch: false,
        type: ResultRowType.Group,
        descriptionText: article.grouping.html,
        descriptionHtml: article.grouping.html,
        isFavorite: false,
        isUnderHeading: false
      });
    }



    if (article.grouping !== undefined && article.grouping?.hierarchy) {
      // set reference mark path
      // article.grouping.subHeadingNumber = this.updateSrcForDataMark(article.grouping.subHeadingNumber ?? "");
      // article.grouping.subHeadingNumber = this.addLinkClassToDataAttributes(article.grouping.subHeadingNumber);
      // // set path for any images
      // article.grouping.subHeadingNumber = this.updateImageSrcPaths(article.grouping.subHeadingNumber);

      // resultLines.push({
      //   item: article.grouping.subHeadingNumber,
      //   groupingHeading: article.grouping?.html ?? "",
      //   groupingHeadingNumber: article.grouping?.headingNumber ?? '',
      //   article: article.groupingHeading,
      //   isSearchMatch: false,
      //   type: ResultRowType.Group,
      //   descriptionText: article.grouping.subHeadingHtml,
      //   descriptionHtml: article.grouping.subHeadingHtml,
      //   isFavorite: false,
      //   isUnderHeading: false
      // });   

      article.grouping.hierarchy.forEach((element: hierarchy) => {
        element.html = this.highlightSearchTextWithDOM(element.html ?? '', searchText);
        element.html = this.updateSrcForDataMark(element.html ?? '');
        element.html = this.addLinkClassToDataAttributes(element.html);
        element.html = this.updateImageSrcPaths(element.html);
        resultLines.push({
          item: element.number,
          groupingHeading: element.html ?? '',
          groupingHeadingNumber: element.number,
          article: element.html ?? '',
          isSearchMatch: false,
          type: ResultRowType.Group,
          // Might pass just text from backend
          descriptionText: element.html ?? '',
          descriptionHtml: element.html ?? '',
          isFavorite: false,
          isUnderHeading: false
        });
      });

    }

    // Use this for displaying more data button
    if (article.headingDataAbove) {
      resultLines.push({
        item: article.groupingItemNumber,
        groupingHeading: article.grouping?.html ?? "",
        groupingHeadingNumber: article.grouping?.headingNumber ?? '',
        article: "Above",
        isSearchMatch: false,
        type: ResultRowType.More,
        descriptionText: "Above",
        isFavorite: false,
        isUnderHeading: false
      });
    }
    //}

    const articleRows = this.transformArticleRowToResultRows(article, searchText);
    resultLines = resultLines.concat(articleRows);

    const articleSubRows = this.transformArticleSubRowToResultRows(article, searchText);
    resultLines = resultLines.concat(articleSubRows);

    if (article.headingDataBelow) {
      resultLines.push({
        item: article.groupingItemNumber,
        groupingHeading: article.grouping?.html ?? "",
        groupingHeadingNumber: article.grouping?.headingNumber ?? '',
        article: "Below",
        isSearchMatch: false,
        type: ResultRowType.More,
        descriptionText: "Below",
        isFavorite: false,
        isUnderHeading: false
      });
    }

    return resultLines;
  }

  /**
   * Transforms a list of groups into a list of ResultRow objects for UI display.
   *
   * @returns {ResultRow[]} An array of ResultRow objects representing the groups and their articles.
   * @param articles
   * @param searchText
   */
  transformArticlesToResultRows(articles: Article[], searchText: string, displaySeeMore: boolean): ResultRow[] {
    let resultLines: ResultRow[] = [];
    if (!articles || articles.length === 0) {
      return [];
    }

    const first = articles[0];

    //if (articles.length == 1)
    if (articles[0].firstOrLast != 1 && displaySeeMore) {
      const previousItemNum = (articles[0].grouping?.headingNumber != '0' && articles[0].grouping?.headingNumber != '' ? articles[0].grouping?.headingNumber : articles[0].itemNumber) ?? "";
      resultLines.push({
        item: previousItemNum,
        groupingHeading: articles[0].descriptionText ?? "",
        groupingHeadingNumber: articles[0].grouping?.headingNumber ?? '',
        article: "Above",
        isSearchMatch: false,
        type: ResultRowType.More,
        descriptionText: "See Previous",
        isFavorite: false,
        isUnderHeading: false
      });
    }
    // 1) The group row
    resultLines = resultLines.concat(this.getGroupingResultRows(first, searchText));



    // 2) The article rows
    for (const article of articles) {
      if (!this.ArticleNumberInGroup(article)) {
        // Use this for displaying more data button

        if (article.headingDataAbove) {
          if (first.grouping !== undefined && displaySeeMore) {
            // resultLines.splice(resultLines.length > 0 ? resultLines.length - 1 : 0, 0, {
            //   item: article.itemNumber,
            //   groupingHeading: article.grouping?.html ?? "",
            //   article: "Above",
            //   isSearchMatch: false,
            //   type: ResultRowType.More,
            //   descriptionText: "Above",
            //   isFavorite: false
            // });
            //}
            // else {
            resultLines.push({
              item: article.itemNumber,
              groupingHeading: article.grouping?.html ?? "",
              groupingHeadingNumber: article.grouping?.headingNumber ?? '',
              article: "Above",
              isSearchMatch: false,
              type: ResultRowType.MoreGroup,
              descriptionText: "See More",
              isFavorite: false,
              isUnderHeading: true
            });
          }
        }
        //if (article.grouping === undefined) {//|| article?.grouping?.headingNumber !== article.itemNumber) {
        const articleRows = this.transformArticleRowToResultRows(article, searchText);
        resultLines = resultLines.concat(articleRows);

        const articleSubRows = this.transformArticleSubRowToResultRows(article, searchText);
        resultLines = resultLines.concat(articleSubRows);
        //}
        // if (article.headingDataBelow) {
        //   resultLines.push({
        //     item: article.itemNumber,
        //     groupingHeading: article.grouping?.html ?? "",
        //     article: "Below",
        //     isSearchMatch: false,
        //     type: ResultRowType.More,
        //     descriptionText: "Below",
        //     isFavorite: false
        //   });
        // }

      }
      else {
        if (!resultLines.some(line => line.item === article.itemNumber)) {
          const articleRows = this.transformArticleRowToResultRows(article, searchText);
          resultLines = resultLines.concat(articleRows);

          const articleSubRows = this.transformArticleSubRowToResultRows(article, searchText);
          resultLines = resultLines.concat(articleSubRows);
        }

      }
    }

    if (articles[articles.length - 1].firstOrLast != 2 && displaySeeMore) {
      if (articles[articles.length - 1].grouping?.headingNumber != "0")
        resultLines.push({
          item: articles[articles.length - 1].itemNumber,
          groupingHeading: articles[articles.length - 1].descriptionText ?? "",
          groupingHeadingNumber: articles[articles.length - 1].grouping?.headingNumber ?? '',
          article: "Below",
          isSearchMatch: false,
          type: ResultRowType.MoreGroup,
          descriptionText: "See More",
          isFavorite: false,
          isUnderHeading: true
        });
      else
        resultLines.push({
          item: articles[articles.length - 1].itemNumber,
          groupingHeading: articles[articles.length - 1].descriptionText ?? "",
          groupingHeadingNumber: articles[articles.length - 1].grouping?.headingNumber ?? '',
          article: "Below",
          isSearchMatch: false,
          type: ResultRowType.More,
          descriptionText: "See Next",
          isFavorite: false,
          isUnderHeading: false
        });
    }
    return resultLines;
  }

  transformArticleRowToResultRows(article: Article, searchText: string): ResultRow[] {
    const resultLines: ResultRow[] = [];
    searchText = searchText.trim();
    if (article == undefined)
      return [];

    // If the article is being updated because of being Favorite/unfavorite
    // than html parsing has already been done
    if (!article.isUpdateFavorite) {
      const text = '';
      const regex = new RegExp(`(${searchText})`, 'gi');

      // Add highlight to search text in descriptionText
      if (searchText !== '') {
        article.descriptionHtml = this.highlightSearchTextWithDOM(article.descriptionHtml, searchText);
      }

      // set reference mark path
      article.descriptionHtml = this.updateSrcForDataMark(article.descriptionHtml);

      // set path for any images
      article.descriptionHtml = this.updateImageSrcPaths(article.descriptionHtml);

      // Set css for links
      article.descriptionHtml = this.addLinkClassToDataAttributes(article.descriptionHtml);

    }

    resultLines.push({
      item: article.itemNumber,
      article: article.descriptionText,
      class: article.classification,
      groupingHeading: article.grouping?.html ?? '',
      groupingHeadingNumber: article.grouping?.headingNumber ?? '',
      descriptionLines: article.descriptionLines,
      descriptionText: article.descriptionText,
      descriptionHtml: article.descriptionHtml,
      isSearchMatch: article.itemNumber === searchText,
      type: ResultRowType.Article,
      isFavorite: article.isFavorite,
      isUnderHeading: false
    });

    return resultLines;
  }

  /**
   * Converts an Article object to a list of ResultRow objects for UI display.
   * @param article
   * @param searchText
   */
  transformArticleSubRowToResultRows(article: Article, searchText: string): ResultRow[] {
    const resultLines: ResultRow[] = [];
    searchText = searchText.trim();


    for (const sub of article.subItems ?? []) {
      const text = '';
      // If the article is being updated because of being Favorite/unfavorite
      // than html parsing has already been done
      if (!article.isUpdateFavorite) {
        const regex = new RegExp(`(${searchText})`, 'gi');
        if (searchText !== '') {
          sub.descriptionHtml = sub.descriptionHtml.replace(regex, '<mark>$1</mark>');
        }

        // set reference mark path
        sub.descriptionHtml = this.updateSrcForDataMark(sub.descriptionHtml);

        sub.descriptionHtml = this.addLinkClassToDataAttributes(sub.descriptionHtml);

        // set path for any images
        sub.descriptionHtml = this.updateImageSrcPaths(sub.descriptionHtml);
      }
      resultLines.push({
        item: sub.item,
        article: sub.description,
        descriptionText: sub.description,
        descriptionHtml: sub.descriptionHtml,
        groupingHeading: article.grouping?.html ?? '',
        groupingHeadingNumber: article.grouping?.headingNumber ?? '',
        class: sub.classification,
        isSearchMatch: article.itemNumber === searchText,
        type: ResultRowType.Sub,
        itemNumber: article.itemNumber,
        isFavorite: article.isFavorite,
        isUnderHeading: false
      });
    }

    return resultLines;
  }



  /**
   * Converts an ArticleDetails object to a list of ResultRow objects for UI display.
   * @param articleDetails
   */
  transformArticleDetailsToResultRows(articleDetails: Article[]): ResultRow[] {
    let resultLines: ResultRow[] = [];

    if (!articleDetails || articleDetails.length === 0) {
      return [];
    }

    // 1) The group row
    const first = articleDetails[0];

    resultLines = this.getGroupingResultRows(first, '');

    for (const article of articleDetails) {
      if (!this.ArticleNumberInGroup(article)) {

        const articleRow = this.transformArticleRowToResultRows(article, "")[0];
        resultLines.push(articleRow);

        const articleSubRow = this.transformArticleSubRowToResultRows(article, "");
        resultLines.push(...articleSubRow);
      }
    }
    return resultLines;
  }


  //   transformDetailSearchToResultListTab(details: DetailedSearch, searchText: string): SearchResultList[] {
  //   const result: SearchResultList[] = [];
  //   searchText = searchText.trim();
  //   const regex = new RegExp(`(${searchText})`, 'gi');

  //   // Helper to group items by groupHeading
  //   function groupByGroupHeading(items: DetailSearchItem[]): { [key: string]: DetailSearchItem[] } {
  //     return items.reduce((groups, item) => {
  //       const key = item.groupHeading || 'Other';
  //       if (!groups[key]) {
  //         groups[key] = [];
  //       }
  //       groups[key].push(item);
  //       return groups;
  //     }, {} as { [key: string]: DetailSearchItem[] });
  //   }

  //   // Helper to process and group a section
  //   function processSection(section: DetailSearchResult | undefined, columnNames: string[]): void {
  //     if (!section) return;
  //     let items = section.items;
  //     if (searchText !== '') {
  //       items = items.map(item => ({
  //         ...item,
  //         description: item.description.replace(regex, '<mark>$1</mark>')
  //       }));
  //     }
  //     const grouped = groupByGroupHeading(items);
  //     Object.keys(grouped).forEach(groupHeading => {
  //       result.push({
  //         label: section.label + (groupHeading !== 'Other' ? ` - ${groupHeading}` : ''),
  //         ariaLabel: section.label + (groupHeading !== 'Other' ? ` - ${groupHeading}` : ''),
  //         count: grouped[groupHeading].length,
  //         index: section.index,
  //         columnNames,
  //         items: grouped[groupHeading]
  //       });
  //     });
  //   }

  //   processSection(details.articles, ['Article', 'Item']);
  //   processSection(details.indexs, ['Article', 'Item']);
  //   processSection(details.rules, ['Rule', 'Item']);
  //   processSection(details.packages, ['Package', 'Item']);
  //   processSection(details.supplements, ['Supplement', 'Item']);
  //   processSection(details.dockets, ['Docket', 'Item']);

  //   return result;
  // }
  /**
   * Converts a DetailedSearch object to a list of SearchResultList objects for UI display.
   * This is used for transforming the data for the left panel of the detail search
   * This will also highlight the search text in the description fields of the items.
   * @param details
   * @param searchText
   */

  /* eslint-disable @typescript-eslint/prefer-for-of */
  transformDetailSearchToResultListTab(details: DetailedSearch, searchText: string): SearchResultList[] {
    if (!details) {
      return [];
    }

    const result: SearchResultList[] = [];
    searchText = searchText.trim();
    const regex = new RegExp(`(${searchText})`, 'gi');

    // TODO : Add error checking and handling
    if (details.articles) {
      if (searchText !== '') {
        for (let x = 0; x < details.articles.items.length; x++) {
          details.articles.items[x].description = details.articles.items[x].description.replace(
            regex,
            '<mark>$1</mark>',
          );
        }
      }
      const f = this.insertGroupingItemsByGroupHeading(details.articles.items);

      // const groups = this.groupDetailSearchItemsByGroupHeading(details.articles.items);
      // const grouping: Record<string, DetailSearchItem[]> = {};
      // groups.forEach((items, groupHeading) => {
      //   grouping[groupHeading] = items;
      // });
      result.push({
        label: details.articles.label,
        ariaLabel: details.articles.label,
        count: details.articles.count,
        index: details.articles.index,
        columnNames: ['Article', 'Item'],
        items: f
        // grouping: Array.from(this.groupDetailSearchItemsByGroupHeading(details.articles.items).entries()).map(
        //   ([key, value]) => ({ key, value })
        // ),
      });
    }
    if (details.indexs) {
      if (searchText !== '') {
        for (let x = 0; x < details.indexs.items.length; x++) {
          details.indexs.items[x].description = details.indexs.items[x].description.replace(regex, '<mark>$1</mark>');
        }
      }
      result.push({
        label: details.indexs.label,
        ariaLabel: details.indexs.label,
        count: details.indexs.count,
        index: details.indexs.index,
        columnNames: ['Article', 'Item'],
        items: this.insertGroupingItemsByGroupHeading(details.indexs.items),
      });
    }
    if (details.rules) {
      if (searchText !== '') {
        for (let x = 0; x < details.rules.items.length; x++) {
          details.rules.items[x].description = details.rules.items[x].description.replace(regex, '<mark>$1</mark>');
        }
      }
      result.push({
        label: details.rules.label,
        ariaLabel: details.rules.label,
        count: details.rules.count,
        index: details.rules.index,
        columnNames: ['Rule', 'Item'],
        items: this.insertGroupingItemsByGroupHeading(details.rules.items),
      });
    }
    if (details.packages) {
      if (searchText !== '') {
        for (let x = 0; x < details.packages.items.length; x++) {
          details.packages.items[x].description = details.packages.items[x].description.replace(
            regex,
            '<mark>$1</mark>',
          );
        }
      }
      result.push({
        label: details.packages.label,
        ariaLabel: details.packages.label,
        count: details.packages.count,
        index: details.packages.index,
        columnNames: ['Package', 'Item'],
        items: details.packages.items,
      });
    }

    if (details.supplements) {
      if (searchText !== '') {
        for (let x = 0; x < details.supplements.items.length; x++) {
          details.supplements.items[x].description = details.supplements.items[x].description.replace(regex, '<mark>$1</mark>');
        }
      }
      result.push({
        label: details.supplements.label,
        ariaLabel: details.supplements.label,
        count: details.supplements.count,
        index: details.supplements.index,
        columnNames: ['Supplement', 'Date'],
        items: this.insertGroupingItemsByGroupHeading(details.supplements.items)
      });
    }

    if (details.dockets) {
      if (searchText !== '') {
        for (let x = 0; x < details.dockets.items.length; x++) {
          details.dockets.items[x].description = details.dockets.items[x].description.replace(regex, '<mark>$1</mark>');
        }
      }
      result.push({
        label: details.dockets.label,
        ariaLabel: details.dockets.label,
        count: details.dockets.count,
        index: details.dockets.index,
        columnNames: ['Docket', 'Item'],
        items: this.insertGroupingItemsByGroupHeading(details.dockets.items)
      });
    }

    return result;
  }
  /**
 * Inserts a grouping item before the first occurrence of each different groupHeading in the array.
 * The grouping item will have only the groupHeading set, and other fields can be set as needed.
 */
  insertGroupingItemsByGroupHeading(items: DetailSearchItem[]): DetailSearchItem[] {
    const result: DetailSearchItem[] = [];
    let lastGroupHeading: string | undefined;

    for (const item of items) {
      if (item.groupHeading !== lastGroupHeading) {
        // Insert a grouping item before the first item of a new groupHeading
        result.push({
          itemNumber: '',
          itemNumbers: [], // or any placeholder
          groupHeading: item.groupHeading,
          description: '', // or something like `Group: ${item.groupHeading}`
          publicationId: undefined,
          pageNumber: undefined,
          isGrouping: true, // Indicate this is a grouping item
          isCanceledReference: false,
          effectiveDate: item.effectiveDate ?? undefined //specifically needed for supplements
        });
        lastGroupHeading = item.groupHeading;
      }
      result.push(item);
    }

    return result;
  }
  /**
   * Groups an array of DetailSearchItem by their groupHeading property.
   * Returns a Map where the key is the groupHeading and the value is an array of DetailSearchItem.
   */
  groupDetailSearchItemsByGroupHeading(items: DetailSearchItem[]): Map<string, DetailSearchItem[]> {
    const grouped = new Map<string, DetailSearchItem[]>();
    items.forEach(item => {
      const key = item.groupHeading || 'Other';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(item);
    });
    return grouped;
  }
  /**
   * Updates the 'src' and 'class' attributes of elements with the 'data-mark' attribute in the given HTML string.
   * @param htmlString The HTML string to be updated.
   * @returns The updated HTML string with modified 'src' and 'class' attributes
   * */
  updateSrcForDataMark(htmlString: string) {
    // Parse the HTML string into a DOM structure
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    // Find all elements with the 'data-mark' attribute
    const elements = doc.querySelectorAll('[data-mark]');

    // Update the 'src', 'class' attributes for each element
    elements.forEach((element) => {
      // Only set src if the element is an <img>
      if (element.tagName.toLowerCase() === 'img') {
        element.classList.add('inline-data-image', 'link');
        element.setAttribute('height', '13');
        const currentSrc = element.getAttribute('src') || '';
        if (currentSrc.lastIndexOf('/') + 1 == 0) {
          element.setAttribute('src', `assets/icons/ref-marks/${currentSrc}`);
        }
      }
      else {
        element.classList.add('link');
      }
    });

    // Serialize the updated DOM back to an HTML string
    return doc.body.innerHTML;
  }

  /**
 * Highlights the search text within <p> tags using the <mark> tag, preserving any existing markup.
 * @param htmlString The HTML string to search within.
 * @param searchText The text to highlight.
 * @returns The updated HTML string with the search text wrapped in <mark> tags.
 */
  highlightSearchTextWithDOM(htmlString: string, searchText: string): string {
    if (!searchText.trim()) {
      return htmlString; // Return the original string if searchText is empty
    }

    // Parse the HTML string into a DOM structure
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');

    // Find all <p> tags
    const paragraphs = doc.querySelectorAll('p');

    paragraphs.forEach((paragraph) => {
      // Use a recursive function to wrap matching text nodes with <mark>
      // Check to make sure <mark> is not already present
      if (paragraph.querySelectorAll('mark').length == 0)
        this.highlightTextNode(paragraph, searchText);
    });

    // Serialize the updated DOM back to an HTML string
    return doc.body.innerHTML;
  }

  /**
   * Recursively highlights matching text nodes within an element.
   * @param element The DOM element to search within.
   * @param searchText The text to highlight.
   */
  private highlightTextNode(element: Node, searchText: string): void {
    const regex = new RegExp(`(${searchText})`, 'gi');

    element.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        // If it's a text node, replace the matching text with <mark>
        const textContent = child.textContent || '';
        if (regex.test(textContent)) {
          const highlightedHTML = textContent.replace(regex, '<mark>$1</mark>');
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = highlightedHTML;

          // Replace the text node with the new highlighted nodes
          while (tempDiv.firstChild) {
            element.insertBefore(tempDiv.firstChild, child);
          }
          element.removeChild(child);
        }
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        // If it's an element node, recurse into it
        this.highlightTextNode(child, searchText);
      }
    });
  }

  /**
 * Updates image source paths in the given description text.
 * @param descriptionText The original description text containing image paths.
 * @param apiUrl The base API URL to replace the image source paths.
 * @returns The updated description text with modified image source paths.
 */
  private updateImageSrcPaths(descriptionText: string): string {
    // const parser = new DOMParser();
    // const doc = parser.parseFromString(descriptionText, 'text/html');
    // const imgTags = doc.querySelectorAll('img');
    // // You can now use imgTags as a NodeList of all <img> elements
    // this.fileDownloadService
    //   .FileByte(`search/image?name=3b59420b5f554495bef2ecef34a69019.png`)
    //   .then((response: any) => {
    //     this.isDownloadingPublication = false;
    //     if (typeof FileReader !== 'undefined') {
    //       const reader = new FileReader();

    //       reader.onload = (e: any) => {
    //         const imgURL = URL.createObjectURL(e);
    //         const imgElement = doc.querySelectorAll('img')[0];
    //         if (imgElement && imgElement instanceof HTMLImageElement) {
    //           imgElement.src = imgURL;
    //         }
    //       };

    //       reader.readAsArrayBuffer(response);


    //     }
    //   })
    //   .catch((error: any) => console.error('Error fetching image:', error));
    // return descriptionText;

    // Update image source paths
    descriptionText = descriptionText.replace(/src="assets\/images\/pub\//g, `src="${environment.apiUrl}search/image?name=`);
    descriptionText = descriptionText.replace(/src="images\//gi, `src="${environment.apiUrl}search/image?name=`);

    // Remove height property and add padding from style attributes
    descriptionText = descriptionText.replace(/style="([^"]*?)"/gi, (match, styleContent) => {
      // Remove height property
      let cleanedStyle = styleContent.replace(/height:[^;]*;?/gi, '');

      // Add padding-top and padding-bottom
      cleanedStyle += cleanedStyle && !cleanedStyle.endsWith(';') ? ';' : '';
      cleanedStyle += 'padding-top:1rem;padding-bottom:1rem';

      // Clean up multiple semicolons and trim
      cleanedStyle = cleanedStyle.replace(/;{2,}/g, ';').replace(/^;|;$/g, '');

      return `style="${cleanedStyle}"`;
    });

    return descriptionText;
  }

  /**
 * Adds the 'link' class to elements with data-article, data-package, or data-rule attributes in the HTML string.
 * @param html The HTML string to update.
 * @returns The updated HTML string.
 */
  private addLinkClassToDataAttributes(html: string): string {
    let text = ' data-article';
    let regex = new RegExp(`(${text})`, 'gi');
    html = html.replace(regex, ' class="link" data-article');

    text = ' data-package';
    regex = new RegExp(`(${text})`, 'gi');
    html = html.replace(regex, ' class="link" data-package');

    text = ' data-rule';
    regex = new RegExp(`(${text})`, 'gi');
    html = html.replace(regex, ' class="link" data-rule');

    return html;
  }

  /**
 * Processes the grouping fields of the first article and returns ResultRow objects.
 * @param first The first Article object in the array.
 * @param searchText The search text to highlight.
 * @param groupingHeading The grouping heading string (optional, for subHeadingNumber).
 * @returns ResultRow[] Array of ResultRow objects for the grouping.
 */
  private getGroupingResultRows(
    first: Article,
    searchText: string
  ): ResultRow[] {
    const resultLines: ResultRow[] = [];
    if (first?.grouping === undefined) {
      return []
    }

    if (
      first.grouping?.headingNumber != '0'
      // && first.grouping?.headingNumber !== first.itemNumber
    ) {
      if (first.grouping !== undefined && first.grouping?.headingNumber) {
        first.grouping.html = this.highlightSearchTextWithDOM(first.grouping.html ?? '', searchText);
        first.grouping.html = this.updateSrcForDataMark(first.grouping.html ?? '');
        first.grouping.html = this.addLinkClassToDataAttributes(first.grouping.html);
        first.grouping.html = this.updateImageSrcPaths(first.grouping.html);

        resultLines.push({
          item: first.grouping.headingNumber,
          groupingHeading: first.grouping.html,
          groupingHeadingNumber: first.grouping.headingNumber,
          article: first.grouping.html,
          isSearchMatch: false,
          type: ResultRowType.Group,
          // Might pass just text from backend
          descriptionText: first.grouping.html,
          descriptionHtml: first.grouping.html,
          isFavorite: first.grouping.isFavorite,
          isUnderHeading: false
        });
      }

    }

    if (first.grouping !== undefined && first.grouping?.hierarchy && first.grouping.hierarchy.length > 0) {
      first.grouping.hierarchy.forEach((element: hierarchy) => {
        element.html = this.highlightSearchTextWithDOM(element.html ?? '', searchText);
        element.html = this.updateSrcForDataMark(element.html ?? '');
        element.html = this.addLinkClassToDataAttributes(element.html);
        element.html = this.updateImageSrcPaths(element.html);
        resultLines.push({
          item: element.number,
          groupingHeading: element.html ?? '',
          groupingHeadingNumber: element.number,
          article: element.html ?? '',
          isSearchMatch: false,
          type: ResultRowType.Group,
          // Might pass just text from backend
          descriptionText: element.html ?? '',
          descriptionHtml: element.html ?? '',
          isFavorite: false,
          isUnderHeading: false
        });
      });
    }

    return resultLines;
  }

  private hasHierarchyEqual(article: Article): boolean {
    return article.grouping?.hierarchy?.some(h => h.number === article.itemNumber) ?? false;
  }

  private ArticleNumberInGroup(article: Article): boolean {
    return article.itemNumber === article.grouping?.headingNumber
      || this.hasHierarchyEqual(article);
  }
}
